import { ElementRef, Injectable } from '@angular/core';
import { NavigationService as NavService, NavigationConfig, NavigationDocument, createNavigationService } from 'qp-navigation';

@Injectable({
  providedIn: 'root',
})
export class NavigationLibraryService {
  config: NavigationConfig = {
    enabledebugLogs: false,
    elementName: 'focusable-element',
    horizontalContainerName: 'horizontal-focusable-container',
    verticalContainerName: 'vertical-focusable-container',
    gridContainerName: 'grid-container',
    gridContainerRowName: 'grid-container-row',
    gridListContainerName: 'gridlist-container',
    backButtonName: 'back-btn',
    backButtonKeys: [8, 81, 461, 10009],
    infinityContainerName: 'infinity-focusable-container',
    focusItselfElementName: 'itself-focusable',
    sectionName: 'container-all',
    epgGridContainerName: 'epg-grid-focusable-container',
    epgGridElementName: 'epg-grid-focusable-element',
    containerName: 'focusable-container', //used to focus storefront carousels in vertical, asigned to all the containers
  };
  private navService: NavService;
  constructor() {
    this.navService = createNavigationService(this.config, document as NavigationDocument);
  }

  init(): void {
    this.navService.init();
  }

  blurActiveElement(): void {
    this.navService.blurActiveElement();
  }

  setIndexContainer(contanierClassName: string, index: number): void {
    this.navService.setIndexContainer(contanierClassName, index);
  }

  getIndexContainer(contanierClassName: string): number {
    return this.navService.getIndexContainer(contanierClassName);
  }

  setSelectedIndex(containerClassName: string, index: number): void {
    const containers = document.getElementsByClassName(containerClassName);
    if (containers.length > 0) {
      containers[0].setAttribute('data-selected-index', index.toString());
    }
  }

  focus(delay = 10): void {
    this.navService.focus(delay);
  }

  focusPage(containerName: string, delay = 50): void {
    this.navService.focusPage(containerName, delay);
  }

  focusSectionPage(sectionName: string, containerName = 'focusable-container', delay = 50): void {
    const index = this.getIndexContainer(sectionName);
    if (index === 0) {
      this.navService.focusPage(containerName, delay);
    } else {
      this.navService.focusSection(sectionName, delay);
    }
  }

  focusSection(sectionName: string, delay = 10): void {
    if (!document.getElementById('exitPage')) {
      this.navService.focusSection(sectionName, delay);
    }
  }

  focusContainer(containerClassName: string, isHorizontal: boolean, customIndex?: number, resetIndex = true, delay = 10): void {
    if (isHorizontal) {
      this.navService.focusHorizontalContainer(containerClassName, resetIndex, customIndex, delay);
    } else {
      this.navService.focusVerticalContainer(containerClassName, resetIndex, customIndex, delay);
    }
  }

  focusGridlist(containerClassName: string, index: number) {
    setTimeout(() => {
      const containers = document.getElementsByClassName(containerClassName);
      if (containers.length > 0) {
        const gridlist = containers[0];
        const elements = gridlist.getElementsByClassName('focusable-element');
        if (elements.length > 0 && index >= 0 && index < elements.length) {
          (elements[index] as HTMLElement).focus();
          gridlist.setAttribute('data-index', index.toString());
          this.setHorizontalIndex(index);
        }
      }
    }, 10);
  }

  focusElement(element: HTMLElement, horizontalIndex: number, verticalIndex: number, delay = 0): void {
    this.navService.focusElement(element, horizontalIndex, verticalIndex, delay);
  }

  isFocusLost(): boolean {
    return document.activeElement === document.getElementsByTagName('body')[0];
  }

  getVerticalIndex(): number {
    return this.navService.getVerticalIndex();
  }

  setVerticalIndex(index: number): void {
    this.navService.setVerticalIndex(index);
  }

  getHorizontalIndex(): number {
    return this.navService.getHorizontalIndex();
  }

  setHorizontalIndex(index: number): void {
    this.navService.setHorizontalIndex(index);
  }

  /**
   * add autoscroll class to the card
   * @param card
   */
  addAutoscroll(card: Element): void {
    if (card && card?.getElementsByTagName('h5')[0]?.scrollWidth > card?.getElementsByTagName('h5')[0]?.clientWidth) {
      card.classList.add('autoScroll');
    }
  }

  /**
   * remove autoscroll class from the card
   * @param card
   */
  removeAutoscroll(card: Element): void {
    if (card && card?.getElementsByTagName('h5')[0]?.scrollWidth > card?.getElementsByTagName('h5')[0]?.clientWidth) {
      card.classList.remove('autoScroll');
    }
  }

  /**
   * align elements to the center of the container
   *
   * @param scrollContainer
   */
  centerActiveElement(scrollContainer: ElementRef): void {
    setTimeout(() => {
      if (document.activeElement.tagName === 'BODY') {
        this.focus();
      }
      const parentActiveElement = document.activeElement.parentElement;
      let y = parentActiveElement.offsetTop - window.innerHeight / 2 + parentActiveElement.getBoundingClientRect().height / 2;
      y = Math.round(y);
      if (scrollContainer && scrollContainer.nativeElement) scrollContainer.nativeElement.scrollTop = y;
    }, 200);
  }

  /**
   * Align elements to the top of the container
   *
   * @param scrollContainer
   */
  alignActiveElement(scrollContainer: ElementRef): void {
    const container = scrollContainer.nativeElement;
    const activeElement = document.activeElement as HTMLElement;
    if (container && activeElement) {
      const elementTop = activeElement.offsetTop;
      const containerHeight = container.offsetHeight;
      const scrollTop = elementTop - containerHeight / 2;
      container.scrollTop = scrollTop - 10;
    }
  }

  /**
   * Set internal storefront index
   *
   * @param internalIndex
   */
  setInternalSfIndex(internalIndex: string) {
    this.navService.setIndexContainer('page-container', Number(internalIndex));
  }

  /**
   * Get internal storefront index
   * @returns index string
   */
  getInternalSfIndex(): string {
    const pageContainer = document.getElementsByClassName('page-container')[0];
    if (pageContainer) {
      const index = pageContainer.getAttribute('data-index');
      if (!!index) {
        return index;
      }
    }
    return '0';
  }

  restartContainerScroll(containerName: string) {
    const container = document.querySelector(`.${containerName}`);
    if (container) {
      container.scrollTop = 0;
    }
  }
}
